import { CheckOutlined } from '@ant-design/icons';
import { Col, Row, Table } from 'antd';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '../../hooks/MediaQuery.hook';
import { styles } from '../../styles';
import { DEFAULT_LEAGUE_LINK } from '../../utils/constant';
import './index.css';

export const HOME_FAQS = [
  {
    question: 'Does Koostream host live sports broadcasts?',
    answer:
      'No. Koostream publishes fixtures, schedules, standings, and original commentary. We do not host copyrighted television or streaming broadcasts. Licensed coverage is available from official rights holders.',
  },
  {
    question: 'Which sports fixtures can I find on Koostream?',
    answer:
      'Koostream lists soccer, NFL, NBA, NHL, MLB, UFC, boxing, Formula 1, MotoGP, cricket, rugby, and tennis schedules when events are available.',
  },
  {
    question: 'Where can I see Premier League standings?',
    answer:
      'Open the Standings page from the homepage for Premier League and other league points tables.',
  },
  {
    question: 'How do I report copyrighted material?',
    answer:
      'Submit a notice through the DMCA Policy page. Include the URL, a description of the work, and the required legal statements.',
  },
];

export default function MainDescription() {
  const matches = useMediaQuery('(min-width: 768px)');
  const columns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'What you will find',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Listed',
      dataIndex: 'availability',
      key: 'availability',
    },
  ];

  const dataSource = [
    {
      key: 'football',
      category: 'Soccer',
      description: 'Premier League, La Liga, Serie A, Ligue 1, MLS, and UEFA club competitions.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
    {
      key: 'nhl',
      category: 'NHL',
      description: 'National Hockey League game schedules and puck-drop times.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
    {
      key: 'mlb',
      category: 'MLB',
      description: 'Major League Baseball schedules and first-pitch times.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
    {
      key: 'nfl',
      category: 'NFL',
      description: 'National Football League regular-season and playoff schedules.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
    {
      key: 'boxing',
      category: 'Boxing and UFC',
      description: 'Fight cards and event dates for boxing and mixed martial arts.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
    {
      key: 'f1',
      category: 'Formula 1',
      description: 'Grand Prix calendar and session times.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
    {
      key: 'nba',
      category: 'NBA',
      description: 'National Basketball Association game schedules.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
    {
      key: 'cricket',
      category: 'Cricket',
      description: 'Test, ODI, and T20 fixtures when listed.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
    {
      key: 'rugby',
      category: 'Rugby',
      description: 'Rugby union and rugby league match schedules.',
      availability: (
        <span style={styles.flexDisplay}>
          <CheckOutlined />
        </span>
      ),
    },
  ];

  return (
    <div className="home-details no-web-display" style={matches ? { padding: '20px' } : {}}>
      <h1 className="heading">
        <span className="site-title">Koostream</span> — sports fixtures, schedules, and news
      </h1>
      <p className="card-description">
        <span className="collection" style={{ marginLeft: '12px' }}>
          What is Koostream?
        </span>
        Koostream is an independent sports information site. We publish upcoming fixtures, kickoff
        times, league standings, and original commentary covering soccer, the NFL, NBA, NHL, MLB,
        UFC, boxing, Formula 1, cricket, rugby, and tennis. We are not a licensed broadcaster and we
        do not host copyrighted game video. Official live coverage belongs to the leagues and their
        authorized media partners.
      </p>
      <p className="card-description">
        Use the homepage to see today’s events, open a sport from the menu, or jump to{' '}
        <Link to={DEFAULT_LEAGUE_LINK}>Premier League fixtures</Link> and{' '}
        <Link to="/leagues/standings">league standings</Link>. Blog articles are written for this
        site. Images used in articles are for editorial illustration; rights holders can request
        removal through our <Link to="/dmca">DMCA Policy</Link>.
      </p>

      <ul className="heading-list">
        <li>
          <p className="collection">
            <Link to={DEFAULT_LEAGUE_LINK}>Soccer fixtures</Link>
          </p>
          <p className="card-description">
            Soccer remains the most-followed sport on Koostream. We list domestic league and cup
            dates, UEFA club competitions, and international tournaments when they are in season.
            Open a match page for kickoff time and team details. For licensed video, subscribe to
            official league or network services in your region.
          </p>
        </li>
        <li>
          <p className="collection">
            <Link to="/fixtures/NFL/NFL">NFL schedule</Link>
          </p>
          <p className="card-description">
            The NFL calendar covers regular-season games, the postseason, and Super Bowl week when
            those events are listed. Kickoff times help you plan around AFC and NFC matchups. NFL
            RedZone and other branded packages are products of official rights holders, not of
            Koostream.
          </p>
        </li>
        <li>
          <p className="collection">
            <Link to="/fixtures/NBA/NBA">NBA</Link>, <Link to="/fixtures/UFC/UFC">UFC</Link>, and{' '}
            <Link to="/fixtures/F1/F1">Formula 1</Link>
          </p>
          <p className="card-description">
            Basketball tip-off times, UFC fight cards, boxing bouts, and Formula 1 session schedules
            appear when data is available. Event titles identify the competition; they are not a
            claim that Koostream exhibits the live program.
          </p>
        </li>
        <li>
          <p className="collection">How to follow a match on Koostream</p>
          <p className="card-description">
            On the homepage, fixtures are grouped by competition. Premier League and other top
            European leagues typically appear near the top on matchdays. Select a listing for more
            detail, or browse <Link to={DEFAULT_LEAGUE_LINK}>league pages</Link> and{' '}
            <Link to="/fixtures/team/Liverpool">team pages</Link> for a full schedule.
          </p>
        </li>
        <li>
          <Row gutter={24}>
            <p className="collection" style={{ marginLeft: '12px' }}>
              Sports calendars on Koostream
            </p>
            <Col span={24}>
              <Table dataSource={dataSource} columns={columns} pagination={false} />
            </Col>
          </Row>
        </li>
        <li>
          <p className="collection">
            <Link to="/leagues/standings">Premier League table and standings</Link>
          </p>
          <p className="card-description">
            The standings page shows the current points table for the Premier League and other
            popular soccer leagues when data is available. Tables are informational and may lag
            official sources.
          </p>
        </li>
        <li>
          <p className="collection">Is Koostream affiliated with the leagues?</p>
          <p className="card-description">
            No. Koostream is independent. We are not endorsed by FIFA, UEFA, the Premier League, the
            NFL, the NBA, the UFC, Formula 1, or any other league or broadcaster. Trademarks appear
            only to identify events.
          </p>
        </li>
        <li>
          <p className="collection">Copyright</p>
          <p className="card-description">
            Original writing on this site is owned by Koostream. We do not claim ownership of league
            footage, photographs owned by others, or third-party trademarks. To report alleged
            infringement, follow the <Link to="/dmca">DMCA Policy</Link>. See also our{' '}
            <Link to="/terms">Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>
        </li>
        <li>
          <p className="collection">Frequently asked questions</p>
          {HOME_FAQS.map((faq) => (
            <div key={faq.question}>
              <p className="collection" style={{ fontSize: '1rem' }}>
                {faq.question}
              </p>
              <p className="card-description">{faq.answer}</p>
            </div>
          ))}
        </li>
      </ul>
    </div>
  );
}
